import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import { pick } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { clsb, clsx } from 'js/utils/classNames.util';

import * as Types from '../../types';
import { useDynamicReducerSelector } from '../../store';

import style from './CarouselItem.scss';

export const CarouselItemBare: React.FC<React.PropsWithChildren<Types.ICarouselItemProps>> = (props) => {
    const {
        children,
        realIndex,
        className,
        classNames,
        focusedItem,
        setFocusedItem
    } = props;
    const slideRef = useRef<HTMLDivElement>(null);
    const translate = useTranslations('playtech.mobile.system.carousel.');
    const { itemWidth, itemShift, activeSlide } = useDynamicReducerSelector(pick(['itemWidth', 'itemShift', 'activeSlide']));
    const [isItemVisible, setIsItemVisible] = useState<boolean>(true);

    const onKeyUp = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        const { key } = event;

        if (key === 'Tab' && event.target === slideRef.current) {
            setFocusedItem(realIndex);
        }
    }, [realIndex, setFocusedItem]);

    useEffect(() => {
        const $slide = slideRef.current;

        if (!$slide) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]): void => {
                entries.forEach((entry: IntersectionObserverEntry) => setIsItemVisible(entry.isIntersecting));
            },
            { root: $slide.parentElement?.parentElement }
        );

        observer.observe($slide);

        return () => {
            observer.unobserve($slide);
        };
    }, []);

    useEffect(() => {
        if (focusedItem === realIndex) {
            slideRef.current?.focus();
        }
    }, [focusedItem, realIndex]);

    return (
        <div
            tabIndex={0}
            role="group"
            ref={slideRef}
            onKeyUp={onKeyUp}
            {...!isItemVisible && { inert: '' }}
            aria-label={translate('aria-label.carouselItem')}
            style={{ width: `${itemWidth}px`, left: `${itemShift}%` }}
            className={clsx([
                clsb([style, classNames], ['carouselItem']),
                className,
                'cms-carousel-item',
                `cms-carousel-item-${realIndex}`,
                isItemVisible && activeSlide === realIndex ? 'cms-carousel-item-active' : ''
            ])}
        >
            {children}
        </div>
    );
};

export const CarouselItem = memo(CarouselItemBare);
